/**
 * IndexedDB, promise-wrapped, one database per signed-in user (docs/52 §5: the offline cache is per user and is
 * cleared on sign out). Stores: `cache` (last responses), `outbox` (queued sends) and `blobs` (photos waiting to
 * upload). Every call fails soft: private windows and storage eviction (docs/54 M2) mean the app runs without it.
 */

const VERSION = 1;
const STORES = ['cache', 'outbox', 'blobs'] as const;
export type StoreName = (typeof STORES)[number];

let dbName: string | null = null;
let dbp: Promise<IDBDatabase | null> | null = null;

export function useDatabaseFor(userId: string | null): void {
  const next = userId ? `ff-${userId}` : null;
  if (next === dbName) return;
  dbName = next;
  dbp = null;
}

function open(): Promise<IDBDatabase | null> {
  if (!dbName || typeof indexedDB === 'undefined') return Promise.resolve(null);
  if (dbp) return dbp;
  const name = dbName;
  dbp = new Promise((resolve) => {
    try {
      const req = indexedDB.open(name, VERSION);
      req.onupgradeneeded = () => {
        for (const s of STORES) if (!req.result.objectStoreNames.contains(s)) req.result.createObjectStore(s);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return dbp;
}

function run<T>(store: StoreName, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T | undefined> {
  return open().then(
    (db) =>
      new Promise<T | undefined>((resolve) => {
        if (!db) return resolve(undefined);
        try {
          const req = fn(db.transaction(store, mode).objectStore(store));
          req.onsuccess = () => resolve(req.result);
          req.onerror = () => resolve(undefined);
        } catch {
          resolve(undefined);
        }
      }),
  );
}

export const idb = {
  get: <T>(store: StoreName, key: string) => run<T>(store, 'readonly', (s) => s.get(key) as IDBRequest<T>),
  set: (store: StoreName, key: string, value: unknown) => run(store, 'readwrite', (s) => s.put(value, key)),
  del: (store: StoreName, key: string) => run(store, 'readwrite', (s) => s.delete(key)),
  all: <T>(store: StoreName) => run<T[]>(store, 'readonly', (s) => s.getAll() as IDBRequest<T[]>).then((v) => v ?? []),
  clear: (store: StoreName) => run(store, 'readwrite', (s) => s.clear()),
};

/** Sign out: drop this user's whole database. */
export async function wipeUserData(): Promise<void> {
  const name = dbName;
  const db = await dbp;
  db?.close();
  dbp = null;
  if (name && typeof indexedDB !== 'undefined') {
    await new Promise<void>((r) => {
      const req = indexedDB.deleteDatabase(name);
      req.onsuccess = req.onerror = req.onblocked = () => r();
    });
  }
}
